//# TODO: 2507500 - change file name to be generic and remove the "temporal" part
// Constants
import { culturesMap, LanguageSaturday, LanguageSunday, LocaleRegex, RegionSaturday, RegionSunday } from "@ms/uno-constants/lib/local/CultureConstants";
import { DayOfWeek, DueDateCategory, RelativeWeekIndex } from "@ms/uno-constants/lib/local/DateConstants";
// Errors
import { ArgumentError } from "@ms/uno-errors/lib/local/errors";
// Utilities
import { format } from "@fluentui/utilities";
import { Temporal } from "@js-temporal/polyfill";
// Constants for date parsing and formatting - chosen for safety and consistency
const PLACEHOLDER_DATE = {
    YEAR: 2000,
    MONTH: 1,
    DAY: 2
};
// Gregorian calendar constants
const MONTHS_IN_YEAR = 12;
/**
 * Get the default due date for a due date category using Temporal
 * @param dueDateCategory The due date category
 */ export function getDefaultDueDate(dueDateCategory) {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    switch(dueDateCategory){
        case DueDateCategory.Late:
            // Yesterday
            return toUTC10am(now.subtract({
                days: 1
            }));
        case DueDateCategory.Today:
            // Current day
            return toUTC10am(now);
        case DueDateCategory.Tomorrow:
            // End of current day + 1 day
            return toUTC10am(now.add({
                days: 1
            }));
        case DueDateCategory.ThisWeek:
            // Last day of range calculated for this week
            return toUTC10am(now.add({
                days: 7 - now.dayOfWeek
            }));
        case DueDateCategory.NextWeek:
            // Last day of range calculated for next week
            return toUTC10am(now.add({
                days: 14 - now.dayOfWeek
            }));
        case DueDateCategory.Future:
            // First day of range calculated for future
            return toUTC10am(now.add({
                days: 15 - now.dayOfWeek
            }));
        case DueDateCategory.NoDate:
        default:
            return null;
    }
}
/**
 * Random date generator using Temporal
 * @param start Start of range for date in milliseconds
 * @param end End of range for date in milliseconds
 */ export function randomDate(start, end) {
    if (end < start) {
        throw new ArgumentError("start", "start must be < end");
    }
    const randomTimestamp = Math.floor(start + Math.random() * (end - start));
    // Convert timestamp to Temporal
    return Temporal.Instant.fromEpochMilliseconds(randomTimestamp).toZonedDateTimeISO("UTC");
}
/**
 * Generate a random Temporal.PlainDateTime prior to the specified timestamp
 * @param timestamp The timestamp to generate a random date before
 */ export function generateRandomDateBefore(timestamp) {
    // Convert to milliseconds for consistent calculation
    const endTime = timestamp.toInstant().epochMilliseconds;
    const startTime = endTime - 10000000000;
    // Generate random date using our existing randomDate function
    return randomDate(startTime, endTime);
}
/**
 * Check if two Temporal.PlainDateTime are on the same day (ignore time)
 * @param date1 First date
 * @param date2 Second date
 */ export function isSameDay(date1, date2) {
    if (date1 == null && date2 == null) {
        return true;
    }
    if (date1 == null || date2 == null) {
        return false;
    }
    // Convert both dates to UTC before comparing to handle timezone differences
    const date1UTC = date1.toInstant().toZonedDateTimeISO("UTC");
    const date2UTC = date2.toInstant().toZonedDateTimeISO("UTC");
    return date1UTC.year === date2UTC.year && date1UTC.month === date2UTC.month && date1UTC.day === date2UTC.day;
}
/**
 * Method to convert Temporal.ZonedDateTime to UTC at 10AM
 * @param originalDate ZonedDateTime object to convert to UTC 10AM
 */ export function toUTC10am(originalDate) {
    // Create a new UTC date with the same year/month/day but at 10AM
    return Temporal.ZonedDateTime.from({
        timeZone: "UTC",
        year: originalDate.year,
        month: originalDate.month,
        day: originalDate.day,
        hour: 10,
        minute: 0,
        second: 0,
        millisecond: 0
    });
}
/**
 * Method to convert Temporal.PlainDateTime to UTC at 10AM
 * @param date Temporal.PlainDateTime object to convert to UTC 10AM.
 */ export function normalizeDate(originalDate) {
    if (originalDate != null) {
        return toUTC10am(originalDate);
    }
    return null;
}
/**
 * Convert a Temporal.ZonedDateTime to a JavaScript Date object with only date components
 * Used for Calendar components that expect Date objects for date-only scenarios
 * @param date The Temporal.ZonedDateTime to convert
 * @returns JavaScript Date object with same year, month, and day, or undefined if input is null
 */ export function createDateFromTemporal(date) {
    if (!date) {
        return undefined;
    }
    return new Date(date.year, date.month - 1, date.day);
}
/**
 * Convert a Temporal.PlainDate to a JavaScript Date object with only date components
 * Used for Calendar components that expect Date objects for date-only scenarios
 * @param plainDate The Temporal.PlainDate to convert
 * @returns JavaScript Date object with same year, month, and day, or null if input is null
 */ export function createDateFromPlainDate(plainDate) {
    if (!plainDate) {
        return null;
    }
    return new Date(plainDate.year, plainDate.month - 1, plainDate.day);
}
/**
 * Convert a JavaScript Date object to Temporal.ZonedDateTime with time set to midnight
 * @param localDate The local Date object
 * @returns Temporal.ZonedDateTime with same year, month, day and time set to midnight in system timezone, or null if input is null
 */ export function createTemporalFromDate(localDate) {
    if (localDate == null) {
        return null;
    }
    // Get system timezone
    const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return Temporal.ZonedDateTime.from({
        year: localDate.getFullYear(),
        month: localDate.getMonth() + 1,
        day: localDate.getDate(),
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
        timeZone: systemTimeZone
    });
}
/**
 * Method to format a Temporal.PlainDateTime into locale specific string
 * MMM DD if within a year of today or long form if over a year away
 * @param date Temporal.PlainDateTime to format
 * @param [useYear] Use the year even if the date is within a year of today
 * @param [formatToken] Options to format the date
 * @param [locale] Locale to use for formatting
 * @returns Date formatted using the provided options
 */ export function formatDate(date, useYear, formatToken = "L", locale) {
    const now = Temporal.Now.zonedDateTimeISO();
    const target = date instanceof Temporal.Instant ? date.toZonedDateTimeISO(now.timeZoneId) : date;
    const sameYear = now.year === target.year;
    const omitYear = !useYear && sameYear;
    return formatLikeMoment(target, formatToken, locale, {
        omitYear
    });
}
/**
 * Format a Date into a locale-specific string (short form, no year)
 * @param date Date to format
 */ export function formatJSDate(date) {
    const instant = Temporal.Instant.fromEpochMilliseconds(date.getTime());
    const zoned = instant.toZonedDateTimeISO(Intl.DateTimeFormat().resolvedOptions().timeZone);
    return formatDate(zoned, false); // useYear = false
}
/**
 * Format a Date using a custom format token like \"LL\", \"lll\", etc.
 * * @param date Date to format
 */ export function formatJSDateCustom(date, format) {
    const instant = Temporal.Instant.fromEpochMilliseconds(date.getTime());
    return formatLikeMoment(instant, format);
}
/**
 * Format a Date into long locale-specific string including the year
 * @param date Date to format
 */ export function formatJSDateLongForm(date) {
    const instant = Temporal.Instant.fromEpochMilliseconds(date.getTime());
    const zoned = instant.toZonedDateTimeISO(Intl.DateTimeFormat().resolvedOptions().timeZone);
    return formatDate(zoned, true);
}
/**
 * Parse the date from the string using Temporal to ensure using the correct locale
 * @param dateString String to parse date
 * @param strict Whether to use strict parsing or not. Default is true
 */ export function parseDateFromString(dateString, strict = true) {
    if (!dateString?.trim()) {
        return null;
    }
    const plainDate = parseTemporalFromString(dateString, strict);
    if (plainDate) {
        return new Date(plainDate.year, plainDate.month - 1, plainDate.day); // Convert to JS Date
    }
    return null;
}
/**
 * Gets the locale-specific ordering of date components (year, month, day)
 * @returns Tuple of [yearIndex, monthIndex, dayIndex] where each is 0, 1, or 2
 */ function getLocaleDateOrdering() {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    const sampleDate = new Date(2001, 10, 22); // Nov 22, 2001
    const formatter = new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    });
    const parts = formatter.formatToParts(sampleDate);
    const ordering = parts.filter((p)=>p.type === "year" || p.type === "month" || p.type === "day").map((p)=>p.type);
    const yearIndex = ordering.indexOf("year");
    const monthIndex = ordering.indexOf("month");
    const dayIndex = ordering.indexOf("day");
    return [
        yearIndex,
        monthIndex,
        dayIndex
    ];
}
/**
 * Parse the date from a string into a new Temporal object in the correct locale
 * Supports multiple formats:
 * - Full dates: "02/12/2025", "2025/02/12" (with 4-digit year)
 * - Partial dates: "02/12", "2/5" (assumes current year)
 * - 2-digit year: "02/12/26", "70/02/25" (00-50 = 2000-2050, 51-99 = 1951-1999)
 *
 * @param dateString String to parse date
 * @param strict Whether to use strict parsing or not. Default is true
 */ export function parseTemporalFromString(dateString, strict = true) {
    if (!dateString?.trim()) {
        return null;
    }
    const trimmed = dateString.trim();
    // Get locale-specific date component ordering once
    const [yearIndex, monthIndex, dayIndex] = getLocaleDateOrdering();
    // Try full (year-included) locale-aware date first
    const fullRegex = localeDatePattern();
    const fullMatch = fullRegex.exec(trimmed);
    if (fullMatch?.groups) {
        const { year, month, day } = fullMatch.groups;
        const y = Number(year);
        const m = Number(month);
        const d = Number(day);
        try {
            return Temporal.PlainDate.from({
                year: y,
                month: m,
                day: d
            }, strict ? {
                overflow: "reject"
            } : {});
        } catch  {
            return null;
        }
    }
    // ---------------------------------------------------------------------
    // Partial date support (no year) e.g. "3/4", "03-04", locale variant.
    // We infer current year & ordering based on locale format parts.
    // ---------------------------------------------------------------------
    // Accept two numeric components (1-2 digits) separated by common delimiters
    const partialPattern = RegExp("^\\s*(?<first>\\d{1,2})[\\/\\-.\\s]+(?<second>\\d{1,2})\\s*$");
    const partialMatch = partialPattern.exec(trimmed);
    if (partialMatch?.groups) {
        const firstNum = Number(partialMatch.groups.first);
        const secondNum = Number(partialMatch.groups.second);
        // Use locale ordering to determine which is month vs day
        const month = monthIndex < dayIndex ? firstNum : secondNum;
        const day = monthIndex < dayIndex ? secondNum : firstNum;
        const year = Temporal.Now.plainDateISO().year;
        try {
            return Temporal.PlainDate.from({
                year,
                month,
                day
            }, strict ? {
                overflow: "reject"
            } : {});
        } catch  {
            return null;
        }
    }
    // ---------------------------------------------------------------------
    // Three-part date with 2-digit or 4-digit year support
    // Examples: "9/26/24" → "9/26/2024"
    // Respects locale ordering: MDY, DMY, or YMD
    // ---------------------------------------------------------------------
    const threePartPattern = RegExp("^\\s*(?<first>\\d{1,4})[\\/\\-.\\s]+(?<second>\\d{1,2})[\\/\\-.\\s]+(?<third>\\d{1,4})\\s*$");
    const threePartMatch = threePartPattern.exec(trimmed);
    if (threePartMatch?.groups) {
        const firstNum = Number(threePartMatch.groups.first);
        const secondNum = Number(threePartMatch.groups.second);
        const thirdNum = Number(threePartMatch.groups.third);
        // Map the three input values to year/month/day based on locale ordering
        const values = [
            firstNum,
            secondNum,
            thirdNum
        ];
        let year = values[yearIndex];
        const month = values[monthIndex];
        const day = values[dayIndex];
        // Handle 2-digit years: 00-84 = 2000-2084, 85-99 = 1985-1999
        if (year < 100) {
            year += year <= 84 ? 2000 : 1900;
        }
        try {
            return Temporal.PlainDate.from({
                year,
                month,
                day
            }, strict ? {
                overflow: "reject"
            } : {});
        } catch  {
            return null;
        }
    }
    return null;
}
/**
 * Check whether a date is today in system timezone
 * @param date ZonedDateTime to check
 */ export function isToday(date) {
    if (date == null) {
        return false;
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const today = Temporal.Now.zonedDateTimeISO(timeZone).startOfDay();
    const dateStartTime = date.withTimeZone(timeZone).startOfDay();
    return dateStartTime.equals(today);
}
/**
 * Check whether a date is tomorrow in system timezone
 * @param date ZonedDateTime to check
 */ export function isTomorrow(date) {
    if (date == null) {
        return false;
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tomorrow = Temporal.Now.zonedDateTimeISO(timeZone).add({
        days: 1
    }).startOfDay();
    const dateStartTime = date.withTimeZone(timeZone).startOfDay();
    return dateStartTime.equals(tomorrow);
}
/**
 * Check whether a date lies in current week in system timezone
 * @param date ZonedDateTime to check
 */ export function isInCurrentWeek(date) {
    if (date == null) {
        return false;
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const localDate = date.withTimeZone(timeZone);
    const startOfWeek = now.subtract({
        days: now.dayOfWeek - 1
    }).startOfDay();
    const endOfWeek = startOfWeek.add({
        days: 6
    }).with({
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999
    });
    return Temporal.ZonedDateTime.compare(localDate, startOfWeek) >= 0 && Temporal.ZonedDateTime.compare(localDate, endOfWeek) <= 0;
}
/**
 * Check whether a date is after end of next week in system timezone
 * @param date ZonedDateTime to check
 */ export function isAfterEndOfNextWeek(date) {
    if (date == null) {
        return false;
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const localDate = date.withTimeZone(timeZone);
    const endOfNextWeek = now.add({
        days: 7
    }).add({
        days: 6 - now.add({
            days: 7
        }).dayOfWeek
    }).with({
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999
    });
    return Temporal.ZonedDateTime.compare(localDate, endOfNextWeek) > 0;
}
/**
 * Check whether a date lies in next week in system timezone
 * @param date ZonedDateTime to check
 */ export function isInNextWeek(date) {
    if (date == null) {
        return false;
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const localDate = date.withTimeZone(timeZone);
    const startOfNextWeek = now.add({
        days: 7 - now.dayOfWeek + 1
    }).startOfDay();
    const endOfNextWeek = startOfNextWeek.add({
        days: 6
    }).with({
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999
    });
    return Temporal.ZonedDateTime.compare(localDate, startOfNextWeek) >= 0 && Temporal.ZonedDateTime.compare(localDate, endOfNextWeek) <= 0;
}
/**
 * Check whether a date is in the current month in system timezone
 * @param date ZonedDateTime to check
 */ export function isInCurrentMonth(date) {
    if (date == null) {
        return false;
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const localDate = date.withTimeZone(timeZone);
    return now.year === localDate.year && now.month === localDate.month;
}
/**
 * Check whether a date was yesterday in system timezone
 * @param timeStamp ZonedDateTime to check
 */ export function isYesterday(timeStamp) {
    if (timeStamp == null) {
        return false;
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const yesterday = Temporal.Now.zonedDateTimeISO(timeZone).subtract({
        days: 1
    }).startOfDay();
    const dateStartTime = timeStamp.withTimeZone(timeZone).startOfDay();
    return dateStartTime.equals(yesterday);
}
/**
 * Check whether a date was in past few days (between yesterday and 6 days ago) in system timezone
 * @param timeStamp ZonedDateTime to check
 */ export function inPastFewDays(timeStamp) {
    if (timeStamp == null) {
        return false;
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const localDate = timeStamp.withTimeZone(timeZone).startOfDay();
    const startOfYesterday = now.subtract({
        days: 1
    }).startOfDay();
    const startOfLast6ThDay = now.subtract({
        days: 6
    }).startOfDay();
    return Temporal.ZonedDateTime.compare(localDate, startOfYesterday) < 0 && Temporal.ZonedDateTime.compare(localDate, startOfLast6ThDay) > 0;
}
/**
 * Check whether date falls within the last week (7 days)
 * @param date Temporal.ZonedDateTime
 */ export const isWithinPastWeek = (date)=>{
    if (date === null) {
        return false;
    }
    const now = Temporal.Now.zonedDateTimeISO();
    const durationInDays = Math.abs(date.until(now).total({
        unit: "day"
    }));
    return durationInDays < 7;
};
/**
 * Check whether provided Temporal.ZonedDateTime was moments ago
 * @param timeStamp The Temporal.ZonedDateTime to compare
 */ export function inLastMinute(timeStamp) {
    if (timeStamp == null) {
        return false;
    }
    const now = Temporal.Now.zonedDateTimeISO();
    const duration = timeStamp.until(now);
    return Math.abs(duration.total({
        unit: "second"
    })) < 120;
}
/**
 * Check whether date falls within the last minute (45 seconds)
 * @param date Temporal.ZonedDateTime
 */ export const isWithinPastMinute = (date)=>{
    if (!date) return false;
    const now = Temporal.Now.zonedDateTimeISO();
    const seconds = date.until(now).total({
        unit: "second"
    });
    return seconds >= 0 && seconds < 45;
};
/**
 * Check whether provided Temporal.ZonedDateTime was minutes ago
 * @param timeStamp The Temporal.ZonedDateTime to compare
 */ export function inPastFewMinutes(timeStamp) {
    if (timeStamp == null) {
        return false;
    }
    const now = Temporal.Now.zonedDateTimeISO();
    const duration = timeStamp.until(now);
    const minutes = Math.abs(duration.total({
        unit: "minute"
    }));
    return minutes >= 2 && minutes <= 60;
}
/**
 * Check whether provided Temporal.ZonedDateTime was an hour ago
 * @param timeStamp The Temporal.ZonedDateTime to compare
 */ export function inPastHour(timeStamp) {
    if (timeStamp == null) {
        return false;
    }
    const now = Temporal.Now.zonedDateTimeISO();
    const duration = timeStamp.until(now);
    const minutes = Math.abs(duration.total({
        unit: "minute"
    }));
    return minutes >= 61 && minutes <= 90;
}
/**
 * Check whether date falls within the last hour (60 minutes)
 * @param date ZonedDateTime to check
 */ export const isWithinPastHour = (date)=>{
    if (date == null) return false;
    const now = Temporal.Now.zonedDateTimeISO();
    const duration = date.until(now);
    const minutes = duration.total({
        unit: "minutes"
    });
    return minutes < 60;
};
/**
 * Check whether date falls within the last day (24 hours)
 * @param date Temporal.ZonedDateTime
 */ export const isWithinPastDay = (date)=>{
    if (!date) return false;
    const now = Temporal.Now.zonedDateTimeISO();
    const hours = date.until(now).total({
        unit: "hour"
    });
    return hours >= 0 && hours < 24;
};
/**
 * Check whether provided Temporal.ZonedDateTime was hours ago
 * @param timeStamp The Temporal.ZonedDateTime to compare
 */ export function inPastFewHours(timeStamp) {
    if (timeStamp == null) {
        return false;
    }
    const now = Temporal.Now.zonedDateTimeISO();
    const duration = timeStamp.until(now);
    const minutes = Math.abs(duration.total({
        unit: "minute"
    }));
    return minutes >= 91 && minutes <= 480;
}
/**
 * Check whether provided Temporal.ZonedDateTime was a with last year
 * @param timeStamp The Temporal.ZonedDateTime to compare
 */ export function inLastYear(timeStamp) {
    if (timeStamp == null) {
        return false;
    }
    const now = Temporal.Now.zonedDateTimeISO();
    const duration = timeStamp.until(now);
    const years = Math.abs(duration.total({
        unit: "year",
        relativeTo: timeStamp
    }));
    return years < 1;
}
/**
 * Check whether date falls within the last year (365 days)
 * @param date Temporal.ZonedDateTime
 */ export const isWithinPastYear = (date)=>{
    if (!date) return false;
    const now = Temporal.Now.zonedDateTimeISO();
    const days = date.until(now).total({
        unit: "day"
    });
    return days >= 0 && days < 365;
};
/**
 * Compare a timestamp to the current time and return a string indicating about how much time has passed.
 * @param timestamp the temporal timestamp to compare
 */ export const getElapsedTimeApproximation = (timestamp, Strings)=>{
    if (timestamp == null) {
        return "";
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const zdt = timestamp instanceof Temporal.ZonedDateTime ? timestamp : timestamp.toZonedDateTimeISO(timeZone);
    const now = Temporal.Now.zonedDateTimeISO();
    const duration = now.since(zdt, {
        largestUnit: "days"
    });
    if (isWithinPastMinute(zdt)) {
        // if plan was accessed within the past 45 seconds, return "Just now"
        return Strings.LastAccessed_LastAccessedJustNow;
    }
    if (isWithinPastHour(zdt)) {
        // if plan was accessed within the past hour, return "{Minutes}m ago" (minimum: "1m ago")
        const durationInMinutes = duration.minutes <= 2 ? 1 : duration.minutes;
        return format(Strings.LastAccessed_LastAccessedMinutesAgo, durationInMinutes);
    }
    if (isWithinPastDay(zdt)) {
        // if plan was accessed within the past day, return "{Hours}h ago"
        return format(Strings.LastAccessed_LastAccessedHoursAgo, duration.hours);
    }
    if (isWithinPastWeek(zdt)) {
        // if plan was accessed within the past week, return "{DayShort} at {Time AM/PM}"
        return format(Strings.LastAccessed_LastAccessedDayAndTime, formatLikeMoment(zdt, "ddd"), formatLikeMoment(zdt, "LT"));
    }
    const useYear = !isWithinPastYear(zdt);
    return formatDate(zdt, useYear, "ll");
};
/**
 * Map a Temporal.PlainDateTime day to a DayOfWeek enum based on the day being in a range from 0-6.
 * @param temporalDate Temporal.PlainDateTime day
 */ export function getDayOfWeekFromIndex(temporalDate) {
    switch(temporalDate.dayOfWeek){
        case 1:
            return DayOfWeek.Monday;
        case 2:
            return DayOfWeek.Tuesday;
        case 3:
            return DayOfWeek.Wednesday;
        case 4:
            return DayOfWeek.Thursday;
        case 5:
            return DayOfWeek.Friday;
        case 6:
            return DayOfWeek.Saturday;
        case 7:
        default:
            return DayOfWeek.Sunday;
    }
}
/**
 * Map a DayOfWeek enum to a Temporal.PlainDateTime day index.
 * @param dayOfWeek DayOfWeek enum
 */ export function getDayIndexFromDayOfWeek(dayOfWeek) {
    switch(dayOfWeek){
        case DayOfWeek.Monday:
            return 1;
        case DayOfWeek.Tuesday:
            return 2;
        case DayOfWeek.Wednesday:
            return 3;
        case DayOfWeek.Thursday:
            return 4;
        case DayOfWeek.Friday:
            return 5;
        case DayOfWeek.Saturday:
            return 6;
        case DayOfWeek.Sunday:
        default:
            return 7;
    }
}
/**
 * Get the dayOfWeek array for a week, starting on a specific start weekday.
 * @param startDayIndex The zero-based index of the start day of the week. Should be within 0-6.
 */ export function getDaysOfWeekByStartDay(startDayIndex) {
    const daysOfWeek = [
        DayOfWeek.Sunday,
        DayOfWeek.Monday,
        DayOfWeek.Tuesday,
        DayOfWeek.Wednesday,
        DayOfWeek.Thursday,
        DayOfWeek.Friday,
        DayOfWeek.Saturday
    ];
    if (startDayIndex < 0 || startDayIndex > 6) {
        return daysOfWeek;
    }
    const splicedDaysOfWeek = daysOfWeek.splice(startDayIndex);
    return splicedDaysOfWeek.concat(daysOfWeek);
}
/**
 * Map a Temporal.PlainDate day to a RelativeWeekIndex enum with locale support.
 * @param date Temporal.PlainDate date
 */ export function getWeekIndex(date) {
    const firstDayOfMonth = date.with({
        day: 1
    });
    const weekInMonth = Math.ceil((date.day + firstDayOfMonth.dayOfWeek - 1) / 7);
    const totalWeeks = Math.ceil((date.daysInMonth + firstDayOfMonth.dayOfWeek - 1) / 7);
    if (weekInMonth === 1) return RelativeWeekIndex.First;
    if (weekInMonth === 2) return RelativeWeekIndex.Second;
    if (weekInMonth === 3) return RelativeWeekIndex.Third;
    if (weekInMonth === 4 && weekInMonth !== totalWeeks) return RelativeWeekIndex.Fourth;
    return RelativeWeekIndex.Last;
}
/**
 * Map string month to a month index, to handle non-latin based languages for moment not able to generate the month index in en.
 * This function returns 1 based month index.
 * @param month The month string in en
 */ export function getMonthIndexFromMonth(month) {
    const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
    ];
    return months.indexOf(month) + 1;
}
export var TimeRange;
(function(TimeRange) {
    // Last 7 days
    TimeRange["LastWeek"] = "lastWeek";
    // Last 14 days
    TimeRange["LastTwoWeeks"] = "lastTwoWeeks";
    // Last 30 days
    TimeRange["LastMonth"] = "lastMonth";
    // Last 90 days
    TimeRange["LastQuarter"] = "lastQuarter";
})(TimeRange || (TimeRange = {}));
export const timeRangeToDays = (reportingPeriod)=>{
    switch(reportingPeriod){
        case "lastWeek":
            return 7;
        case "lastTwoWeeks":
            return 14;
        case "lastMonth":
            return 30;
        case "lastQuarter":
            return 90;
        default:
            return 0;
    }
};
/**
 * Calculate the difference between a timestamp and now.
 * @param timeStamp - The ZonedDateTime to compare with now
 * @param unit - The unit to return the difference in:
 *   - "second" - Returns difference in seconds
 *   - "minute" - Returns difference in minutes
 *   - "hour" - Returns difference in hours
 *   - "day" - Returns difference in days
 *   - "millisecond" (default) - Returns difference in milliseconds
 */ export function calculateFromNow(timeStamp, unit = "millisecond") {
    if (timeStamp == null) {
        return 0;
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const localDate = timeStamp.withTimeZone(timeZone);
    const duration = now.until(localDate);
    return duration.total({
        unit
    });
}
/**
 * Generates a locale-aware date format pattern, dynamically building a strict regex.
 * This is equivalent to moment.localeData().longDateFormat("L") which:
 * - For 'en-US' returns "MM/DD/YYYY"
 * - For 'fr' returns "DD/MM/YYYY"
 * - For other locales returns their appropriate short date format
 * @returns A RegExp that matches the current locale's short date format.
 */ function localeDatePattern() {
    // Refresh locale in case system setting changed, similar to moment.locale()
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    // Create formatter for numeric dates with 2-digit month/day and 4-digit year
    // This matches moment's "L" format which always uses numbers (not month names)
    const formatter = new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    });
    // Use a sample date to get the format parts
    // moment internally does the same to determine format order
    const sampleDate = new Date(2001, 10, 22); // Example date (Nov 22, 2001)
    const parts = formatter.formatToParts(sampleDate);
    // Build regex pattern that matches moment's "L" format:
    // - Allows whitespace like moment
    // - Captures named groups for year/month/day
    // - Preserves locale-specific separators
    let pattern = "^\\s*"; // Allow leading whitespace like moment
    for (const part of parts){
        if (part.type === "day") {
            pattern += "(?<day>\\d{1,2})"; // DD - Capture 2-digit day
        } else if (part.type === "month") {
            pattern += "(?<month>\\d{1,2})"; // MM - Capture 2-digit month
        } else if (part.type === "year") {
            pattern += "(?<year>\\d{4})"; // YYYY - Capture 4-digit year
        } else if (part.type === "literal") {
            // Escape separators (/, -, ., etc) like moment does
            pattern += part.value.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
        }
    }
    pattern += "\\s*$"; // Allow trailing whitespace like moment
    // Create RegExp that will strictly match the locale's date format
    // eslint-disable-next-line security/detect-non-literal-regexp
    return new RegExp(pattern);
}
/**
 * -------------------------------------------------------------------------
 * Temporal Locale weekday handling
 * These functions are used to calculate the start of the week based on the locale
 * -------------------------------------------------------------------------
 */ /**
 * Take the region and language and calculate the start date
 * @param region region code
 * @param language language code
 * @returns sunday, monday or saturday as DayOfWeek
 */ export function weekStart(region, language) {
    if (region) {
        if (RegionSunday.includes(region?.toLowerCase())) {
            return DayOfWeek.Sunday;
        } else if (RegionSaturday.includes(region?.toLowerCase())) {
            return DayOfWeek.Saturday;
        }
    } else if (language) {
        if (LanguageSunday.includes(language.toLowerCase())) {
            return DayOfWeek.Sunday;
        } else if (LanguageSaturday.includes(language.toLowerCase())) {
            return DayOfWeek.Saturday;
        }
    }
    return DayOfWeek.Monday;
}
/**
 * Take the date locale and calculate the start date
 * @param locale locale string
 * @returns sunday, monday or saturday as DayOfWeek
 */ export function weekStartLocale(locale) {
    // Guard against infinite recursion
    if (!locale) {
        return DayOfWeek.Monday; // Default fallback
    }
    const parts = locale.match(LocaleRegex);
    if (!parts) {
        // If no match and not "en-US", try fallback
        return locale !== "en-US" ? weekStartLocale("en-US") : DayOfWeek.Sunday;
    }
    const localeValidationArr = locale.split("-");
    // Safety check for correct locale format
    if (localeValidationArr[0] !== parts[1] || localeValidationArr.length > 1 && localeValidationArr[localeValidationArr.length - 1] !== parts[4]) {
        // If invalid format and not "en-US", try fallback
        return locale !== "en-US" ? weekStartLocale("en-US") : DayOfWeek.Sunday;
    }
    const region = parts[4];
    const language = parts[1];
    // Handle fallbacks for certain locales
    if (!region && culturesMap.fallbacks[language]) {
        const fallbackLocale = culturesMap.fallbacks[language];
        // Prevent recursive fallback
        return fallbackLocale !== locale ? weekStartLocale(fallbackLocale) : DayOfWeek.Monday;
    }
    return weekStart(region, language);
}
/**
 * Calculates the index of the first day of the week based on the current locale.
 * @param isIndexZero boolean (index = 1 for Temporal objects, 0 for Fluent Calendar controls)
 * @returns Returns index of Sunday, Monday or Saturday as a number (0-6) OR (1-7) based on the parameter.
 */ export function weekStartIndexLocale(isIndexZero = false, locale = Intl.DateTimeFormat().resolvedOptions().locale) {
    const firstDayOfWeek = weekStartLocale(locale);
    if (isIndexZero) {
        return getDayIndexFromDayOfWeek(firstDayOfWeek) % 7; // Ensure it wraps around for 0-6 range
    }
    return getDayIndexFromDayOfWeek(firstDayOfWeek);
}
/**
 * Calculate weekday offset based on current locale
 * @returns offset: -1 for Sunday start, -2 for Saturday start, 0 for Monday start
 */ export function getLocaleWeekdayOffset() {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    const startDay = weekStartLocale(locale);
    switch(startDay){
        case DayOfWeek.Sunday:
            return -1;
        case DayOfWeek.Saturday:
            return -2;
        case DayOfWeek.Monday:
        default:
            return 0;
    }
}
/**
 * Get first day of week in current locale
 * @param date ZonedDateTime to get first day of week for
 * @returns ZonedDateTime set to first day of week in current locale
 */ export function firstDayOfWeek(date) {
    const offset = getLocaleWeekdayOffset();
    return date.startOfDay().subtract({
        days: date.dayOfWeek - 1
    }).add({
        days: offset
    });
}
/**
 * Get last day of week in current locale
 * @param date ZonedDateTime to get last day of week for
 * @returns ZonedDateTime set to last day of week in current locale
 */ export function lastDayOfWeek(date) {
    const offset = getLocaleWeekdayOffset();
    return date.startOfDay().add({
        days: 7 - date.dayOfWeek
    }).add({
        days: offset
    });
}
/**
 * Get locale weekdays in long format
 * @returns Array of weekday names in current locale order
 */ export const localeWeekdaysLong = ()=>{
    const offset = getLocaleWeekdayOffset();
    const weekdays = [
        DayOfWeek.Monday,
        DayOfWeek.Tuesday,
        DayOfWeek.Wednesday,
        DayOfWeek.Thursday,
        DayOfWeek.Friday,
        DayOfWeek.Saturday,
        DayOfWeek.Sunday
    ];
    // Rotate array based on offset
    const resultWeekdays = [];
    const length = weekdays.length;
    for(let i = 0; i < length; i++){
        resultWeekdays[i] = weekdays[(i + 7 + offset) % length];
    }
    return resultWeekdays;
};
/**
 * Get locale month in certain format
 * @param temporalDate Temporal.ZonedDateTime or Temporal.Instant
 * @param format Format of the month name (long, short)
 * @returns Month name in current locale order
 */ export function getMonthName(temporalDate, format = "long") {
    const dateTime = temporalDate instanceof Temporal.Instant ? temporalDate : temporalDate.toInstant();
    return new Intl.DateTimeFormat(Intl.DateTimeFormat().resolvedOptions().locale, {
        month: format
    }).format(new Date(dateTime.epochMilliseconds));
}
/**
 * Get locale weekday in certain format
 * @param temporalDate Temporal.ZonedDateTime or Temporal.Instant
 * @param format Format of the weekday name (long, short)
 * @returns Weekday name in current locale order
 */ export function getWeekdayName(date, format = "long") {
    const { locale, timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const zoned = date instanceof Temporal.Instant ? date.toZonedDateTimeISO(timeZone) : date;
    // Monday-base
    const offset = getLocaleWeekdayOffset();
    const weekdayIndex = (zoned.dayOfWeek - 1 + offset + 7) % 7;
    // Fixed Monday: 2000-01-03, then add weekdayIndex
    const utcDate = new Date(Date.UTC(2000, 0, 3 + weekdayIndex));
    // Format only the weekday in the target locale; lock to UTC to avoid TZ shifts
    return new Intl.DateTimeFormat(locale, {
        weekday: format,
        timeZone: "UTC"
    }).format(utcDate);
}
/**
 * Get all month names in the current locale
 * @param format Format of the month name (long, short)
 * @returns Array of month names in current locale order
 */ export function getAllMonthNames(format = "long") {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    const dtf = new Intl.DateTimeFormat(locale, {
        month: format
    });
    return Array.from({
        length: MONTHS_IN_YEAR
    }, (_, i)=>dtf.format(new Date(PLACEHOLDER_DATE.YEAR, i, 1)));
}
/**
 * Get all weekday names in the current locale
 * @param format Format of the weekday name (long, short)
 * @returns Array of weekday names in current locale order
 */ export function getAllWeekdayNames(format = "long") {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    // use utc to avoid timezone shifting the day
    // e.g. in en-US, 2000-01-02 is Sunday, but in Asia/Shanghai it's already Monday 2000-01-03
    // so we use UTC to avoid this issue
    const dtf = new Intl.DateTimeFormat(locale, {
        weekday: format,
        timeZone: "UTC"
    });
    // Base array starting on Monday, 2000-01-03 is a Monday
    const weekdaysMondayBase = Array.from({
        length: 7
    }, (_, i)=>dtf.format(new Date(Date.UTC(2000, 0, 3 + i))));
    // offset: Mon=0, Sun=-1, Sat=-2
    const offset = getLocaleWeekdayOffset();
    const rot = (7 + offset) % 7; // 0 / 6 / 5
    return rot === 0 ? weekdaysMondayBase : weekdaysMondayBase.slice(rot).concat(weekdaysMondayBase.slice(0, rot));
}
// Please note that the following function is not yet supported for all js runtimes and browsers.Comment it out until it is supported.
/**
 * Get the first day of the week based on the current locale
 * @returns The first day of the week as a number (0-6) where 0 is Sunday
 */ // export function getFirstDayOfWeek(): number {
//     // Get current locale
//     const locale = Intl.DateTimeFormat().resolvedOptions().locale;
//     const localeInfo = new Intl.Locale(locale);
//     // Get locale's first day of week using Intl.Locale
//     return localeInfo.getWeekInfo()?.firstDay ?? 1;
// }
/**
 * -------------------------------------------------------------------------
 * Temporal formatting handling
 * These functions are used to format dates in a locale/zone-aware manner
 * For replacing moment.js formatting
 * -------------------------------------------------------------------------
 */ /**
 * Format a Temporal.ZonedDateTime using moment-like tokens: "L", "LL", "LLL", "LLLL"
 * Note: We do not want to format exactly like moment, Intl is different for some format
 *
 * Examples:
 * const instant = Temporal.Instant.from("2016-10-13T12:00:00Z");
 * const zdt = instant.toZonedDateTimeISO("America/New_York");
 * formatLikeMoment(zdt, "L");     // → "10/13/16"
 * formatLikeMoment(zdt, "LL");    // → "October 13, 2016"
 * formatLikeMoment(zdt, "LLL");   // → "October 13, 2016 at 8:00 AM"
 * formatLikeMoment(zdt, "LLLL");  // → "Thursday, October 13, 2016 at 8:00 AM"
 * formatLikeMoment(zdt, "l");     // → "10/13/16"
 * formatLikeMoment(zdt, "ll");    // → "Oct 13, 2016"
 * formatLikeMoment(zdt, "lll");   // → "Oct 13, 2016 at 8:00 AM"
 * formatLikeMoment(zdt, "llll"); // → "Thu, Oct 13, 2016 at 8:00 AM"
 * formatLikeMoment(zdt, "dd");    // → "T"
 * formatLikeMoment(zdt, "ddd");   // → "Thu"
 * formatLikeMoment(zdt, "dddd"); // → "Thursday"
 * formatLikeMoment(zdt, "d");     // → "4"
 * formatLikeMoment(zdt, "LT");    // → "8:00 AM"
 * @param dateTime Temporal.ZonedDateTime | Temporal.Instant
 * @param formatToken IFormatToken - Format token to use for formatting
 * @param [locale] Optional locale, default "en-US"
 * @param [formatOptions] Optional options for Intl.DateTimeFormat
 * @returns Formatted string
 */ export function formatLikeMoment(dateTime, formatToken, locale, formatOptions) {
    // Get the locale from the system if not provided
    const resolvedLocale = locale || Intl.DateTimeFormat().resolvedOptions().locale;
    const formatMap = {
        L: {
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        },
        LL: {
            dateStyle: "long"
        },
        LLL: {
            dateStyle: "long",
            timeStyle: "short"
        },
        LLLL: {
            dateStyle: "full",
            timeStyle: "short"
        },
        l: {
            month: "numeric",
            day: "numeric",
            year: "numeric"
        },
        ll: {
            dateStyle: "medium"
        },
        lll: {
            dateStyle: "medium",
            timeStyle: "short"
        },
        llll: {
            weekday: "short",
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "numeric"
        },
        dd: {
            weekday: "narrow"
        },
        ddd: {
            weekday: "short"
        },
        dddd: {
            weekday: "long"
        },
        d: {},
        LT: {
            timeStyle: "short"
        },
        D: {
            day: "numeric"
        }
    };
    let options = {
        ...formatMap[formatToken]
    };
    // We only need to support "ll" and "L" for the omitYear option
    if (formatOptions?.omitYear) {
        // Replace preset with explicit field-level control
        if (formatToken === "ll") {
            options = {
                month: "short",
                day: "numeric"
            };
        } else if (formatToken === "L") {
            options = {
                month: "numeric",
                day: "numeric"
            };
        }
    }
    // Check if dateTime is a Temporal.Instant and convert to ZonedDateTime
    if (dateTime instanceof Temporal.Instant) {
        // Get system timezone
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        dateTime = dateTime.toZonedDateTimeISO(timeZone);
    }
    // Case when formatToken is "d" (day of week)
    if (formatToken === "d") {
        // Return numeric day of week (0 = Sunday) manually
        return String(dateTime.dayOfWeek % 7); // Temporal is 1=Mon..7=Sun, Moment is 0=Sun..6=Sat
    }
    if (formatToken === "D") {
        // Return numeric day of month
        return String(dateTime.day);
    }
    // Generate the formatter with the specified locale and options using Intl.DateTimeFormat
    const formatter = new Intl.DateTimeFormat(resolvedLocale, {
        timeZone: dateTime.timeZoneId,
        ...options
    });
    // Fallback: check if format supports Temporal.ZonedDateTime directly
    try {
        return formatter.format(dateTime); // supported in modern runtimes
    } catch  {
        // Fallback to JS Date using epochMilliseconds
        return formatter.format(new Date(dateTime.epochMilliseconds));
    }
}
/**
 * Converts a moment-like input (Date or ISO string) into Temporal.ZonedDateTime,
 * simulating the behavior of moment(date).
 * @param input - A Date object or ISO 8601 string
 * @param [timeZone] - Optional. Defaults to system time zone.
 * @returns Temporal.ZonedDateTime
 */ export function fromMomentLike(input, timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
    const instant = Temporal.Instant.from(typeof input === "string" ? input : input.toISOString());
    return instant.toZonedDateTimeISO(timeZone);
}
/**
 * Retrieves the current date with  formatting options
 * @param options options for date formatting
 * @returns The formatted date components
 */ export function getHumanizedDate(options) {
    const { weekdayFormat = "short", dayFormat = "2-digit", monthFormat = "short", toUpperCase = true, timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone } = options || {};
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    let dayOfWeek = now.toLocaleString(undefined, {
        weekday: weekdayFormat
    });
    const dateOfMonth = now.toLocaleString(undefined, {
        day: dayFormat
    });
    let month = now.toLocaleString(undefined, {
        month: monthFormat
    });
    if (toUpperCase) {
        dayOfWeek = dayOfWeek.toUpperCase();
        month = month.toUpperCase();
    }
    return {
        dayOfWeek,
        dateOfMonth,
        month
    };
}
/**
 * Function to get the current hour
 */ export function getCurrentHour() {
    const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = Temporal.Now.zonedDateTimeISO(systemTimeZone);
    return now.hour;
}
/**
 * Get month name from month index (1-12)
 */ export function getTemporalMonthNameFromMonth(month) {
    return new Temporal.PlainDate(2000, month, 1).toLocaleString(undefined, {
        month: "long"
    });
}
/**
 * Get day name from day of week index (1-7)
 */ export function getTemporalDayNameFromDayOfWeek(dayOfWeek) {
    return new Temporal.PlainDate(2000, 1, 2 + dayOfWeek).toLocaleString(undefined, {
        weekday: "long"
    });
}
